import { ReactNode, useEffect, useMemo } from 'react'
import {
  AtendimentoType,
  formatDateToPtBr,
  initialQueryState,
  OptionsHelper,
  useDebounce,
} from '../../../../../../../_metronic/helpers'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { useListFilters } from '../../core/ListFiltersProvider'
import { useAuth } from '../../../../../auth'
import {
  ActiveFilter,
  ActiveFiltersBar,
  CustomSelect,
  DateInput,
  Input,
  IOption,
  TableFilter,
  TableFilters,
} from '../../../../../../components'
import { useT } from '_metronic/i18n/Metronici18n'

type Props = {
  /** Conteúdo à direita da linha dos campos (ex.: exportar PDF/CSV). */
  actions?: ReactNode
}

export const PersonalAtendimentosListSearchComponent = ({ actions }: Props) => {
  const { currentUser } = useAuth()
  const { updateState } = useQueryRequest()
  const { response, isLoading } = useQueryResponse()
  const { filters, setFilter, clearFilters } = useListFilters()
  const { t } = useT()
  const total = response?.totalElements ?? 0

  // Tipos de atendimento que o próprio profissional atende.
  const optionsTiposAtendimento = useMemo(
    () => OptionsHelper.getOptionsTiposAtendimentoUsuario(currentUser),
    [currentUser]
  )

  const debouncedFilters = useDebounce(filters, 500)

  const search = useMemo(
    () =>
      [
        debouncedFilters.empresa ? `paciente.empresa:${debouncedFilters.empresa.value}` : '',
        debouncedFilters.tipoAtendimento ? `tipo:${debouncedFilters.tipoAtendimento.value}` : '',
        debouncedFilters.estado ? `estado:${debouncedFilters.estado.value}` : '',
        debouncedFilters.especialidade
          ? `especialidade:${debouncedFilters.especialidade.value}`
          : '',
        debouncedFilters.protocolo ? `protocolo~*${debouncedFilters.protocolo.trim()}*` : '',
        debouncedFilters.paciente
          ? `paciente.pessoa.nome~*${debouncedFilters.paciente.trim()}*`
          : '',
        debouncedFilters.solicitacao ? `solicitacao>${debouncedFilters.solicitacao}T00:00:00` : '',
        debouncedFilters.inicio ? `inicio>${debouncedFilters.inicio}T00:00:00` : '',
      ]
        .filter(Boolean)
        .join(','),
    [debouncedFilters]
  )

  useEffect(() => {
    updateState({ ...initialQueryState, search })
  }, [search, updateState])

  // Chips da faixa "Filtros ativos": cada × limpa só o seu campo. Datas puras via split
  // (formatDateToPtBr), sem `new Date`, para não voltar um dia no fuso do Brasil.
  const activeFilters: ActiveFilter[] = [
    filters.paciente.trim() && {
      key: 'paciente',
      label: t('cols.paciente'),
      value: `“${filters.paciente.trim()}”`,
      onRemove: () => setFilter('paciente', ''),
    },
    filters.protocolo.trim() && {
      key: 'protocolo',
      label: t('cols.protocolo'),
      value: `“${filters.protocolo.trim()}”`,
      onRemove: () => setFilter('protocolo', ''),
    },
    filters.empresa && {
      key: 'empresa',
      label: t('empresa.label'),
      value: filters.empresa.label,
      onRemove: () => setFilter('empresa', undefined),
    },
    filters.estado && {
      key: 'estado',
      label: t('cols.estado'),
      value: filters.estado.label,
      onRemove: () => setFilter('estado', undefined),
    },
    filters.tipoAtendimento && {
      key: 'tipoAtendimento',
      label: t('tipoAtendimento.label'),
      value: filters.tipoAtendimento.label,
      onRemove: () => setFilter('tipoAtendimento', undefined),
    },
    filters.especialidade && {
      key: 'especialidade',
      label: t('especialidade.label'),
      value: filters.especialidade.label,
      onRemove: () => setFilter('especialidade', undefined),
    },
    filters.solicitacao && {
      key: 'solicitacao',
      label: 'Agendados a partir de',
      value: formatDateToPtBr(filters.solicitacao),
      onRemove: () => setFilter('solicitacao', ''),
    },
    filters.inicio && {
      key: 'inicio',
      label: 'Iniciados a partir de',
      value: formatDateToPtBr(filters.inicio),
      onRemove: () => setFilter('inicio', ''),
    },
  ].filter((filter): filter is ActiveFilter => Boolean(filter))

  const moreFiltersActiveCount = [
    filters.empresa,
    filters.estado,
    filters.tipoAtendimento,
    filters.especialidade,
    filters.solicitacao,
    filters.inicio,
  ].filter(Boolean).length

  return (
    <TableFilters
      actions={actions}
      moreFiltersActiveCount={moreFiltersActiveCount}
      moreFilters={
        <>
          <TableFilter label={t('empresa.label')} wide>
            <CustomSelect
              entity='empresa'
              onChange={(empresa?: IOption<number>) => setFilter('empresa', empresa ?? undefined)}
              fetchOptions={OptionsHelper.getOptionsEmpresas}
              value={filters.empresa}
            />
          </TableFilter>
          <TableFilter label={t('especialidade.label')} wide>
            <CustomSelect
              placeholder='Selecione uma especialidade'
              fetchOptions={OptionsHelper.getOptionsEspecialidades}
              value={filters.especialidade}
              onChange={(especialidade?: IOption<number>) =>
                setFilter('especialidade', especialidade ?? undefined)
              }
            />
          </TableFilter>
          <TableFilter label={t('cols.estado')}>
            <CustomSelect
              placeholder='Selecione um estado'
              onChange={(estado?: IOption<number>) => setFilter('estado', estado ?? undefined)}
              fetchOptions={OptionsHelper.getOptionsEstadosAtendimento}
              value={filters.estado}
            />
          </TableFilter>
          <TableFilter label={t('tipoAtendimento.label')}>
            <CustomSelect
              placeholder='Selecione um tipo'
              options={optionsTiposAtendimento}
              value={filters.tipoAtendimento}
              onChange={(tipo?: IOption<AtendimentoType>) =>
                setFilter('tipoAtendimento', tipo ?? undefined)
              }
            />
          </TableFilter>
          <TableFilter label='Agendados a partir de'>
            <DateInput
              value={filters.solicitacao}
              onChange={(e) => setFilter('solicitacao', e.target.value)}
            />
          </TableFilter>
          <TableFilter label='Iniciados a partir de'>
            <DateInput
              value={filters.inicio}
              onChange={(e) => setFilter('inicio', e.target.value)}
            />
          </TableFilter>
        </>
      }
      footer={
        <ActiveFiltersBar
          filters={activeFilters}
          onClearAll={clearFilters}
          summary={
            isLoading
              ? 'Buscando…'
              : `${total} ${total === 1 ? 'atendimento encontrado' : 'atendimentos encontrados'}`
          }
        />
      }
    >
      <TableFilter label={t('cols.paciente')} wide>
        <Input
          placeholder='Digite o nome do paciente'
          value={filters.paciente}
          onChange={(e) => setFilter('paciente', e.target.value)}
        />
      </TableFilter>
      <TableFilter label={t('cols.protocolo')}>
        <Input
          placeholder='Digite o protocolo'
          value={filters.protocolo}
          onChange={(e) => setFilter('protocolo', e.target.value)}
        />
      </TableFilter>
    </TableFilters>
  )
}
